import { isEmpty, merge } from 'lodash-es';
import { Children, type ReactNode } from 'react';

import { Intent, IntentProps } from '../../../core/slots/Intent/Intent.js';
import { isSlot } from '../../../core/utils/is-slot.js';
import type { AnnotationsTooltipConfig } from '../../core/components/annotations/components/tooltip/types/annotations-tooltip-props.js';
import type { AnnotationsMarkerConfig } from '../../core/components/annotations/types/annotations-marker-props.js';
import type { AnnotationsTrackConfig } from '../../core/components/annotations/types/annotations-track-props.js';
import { populateMissingMarkerProps } from '../../core/components/annotations/utils/populate-missing-marker-props.js';
import { populateMissingTrackProps } from '../../core/components/annotations/utils/populate-missing-track-props.js';
import { ChartInteractions } from '../../core/slots/chart-interactions/ChartInteractions.js';
import { ColorRule, isValidColorRule } from '../../core/slots/ColorRule.js';
import { ChartToolbar } from '../../core/slots/toolbar/ChartToolbar.js';
import { DownloadData } from '../../core/slots/toolbar/DownloadData.js';
import { ChartInteractionsConfig } from '../../core/types/interactions.js';
import { getConfigPlusDefaultOrEmpty } from '../../core/utils/interactions.js';
import { iterateChartInteractionsSlots } from '../../core/utils/iterate-chart-interactions-slots.js';
import { Annotations } from '../slots/annotations/Annotations.js';
import { Marker } from '../slots/annotations/Marker.js';
import { AnnotationsTooltip } from '../slots/annotations/Tooltip.js';
import { Track } from '../slots/annotations/Track.js';
import { HistogramChartDownloadCSVSlot } from '../slots/DownloadCSV.js';
import { Legend } from '../slots/Legend.js';
import { Pan } from '../slots/Pan.js';
import { Select } from '../slots/Select.js';
import { ThresholdIndicator } from '../slots/ThresholdIndicator.js';
import { Toolbar } from '../slots/Toolbar.js';
import { Tooltip } from '../slots/Tooltip.js';
import { XAxis } from '../slots/XAxis.js';
import { YAxis } from '../slots/YAxis.js';
import { Zoom } from '../slots/Zoom.js';
import {
  HistogramChartSlots,
  HistogramChartToolbarConfig,
  HistogramChartToolbarProps,
} from '../types/histogram-props.js';

const slotInteractionsDefaultValues = {
  zoom: { enabled: true },
  zoomX: { enabled: true },
  pan: { enabled: true },
};

const chartInteractionsDefaultValues = {
  zoom: { enabled: false },
  zoomX: { enabled: false },
  pan: { enabled: false },
};

const getInteractionsConfig = (
  chartConfig: ChartInteractionsConfig,
  slotsConfig: ChartInteractionsConfig,
) => {
  const chartApiValues = getConfigPlusDefaultOrEmpty(
    chartInteractionsDefaultValues,
    chartConfig,
  );
  const slotsApiValues = getConfigPlusDefaultOrEmpty(
    slotInteractionsDefaultValues,
    slotsConfig,
  );
  return merge({}, chartApiValues, slotsApiValues);
};

const disabledOnlyWhenSpecified = ({ disabled }: { disabled?: boolean }) =>
  disabled;

export const iterateSlots = (children: ReactNode): HistogramChartSlots => {
  let slots: HistogramChartSlots = {
    legend: {},
    xAxis: {},
    yAxis: [],
    tooltip: {},
    toolbar: {},
    interactions: {},
    thresholds: [],
    colorRules: [],
  };

  // Config for the new Toolbar API Based on slots
  let slotToolbar: HistogramChartToolbarProps = {};
  // Config for the old API Toolbar to be removed with APPDEV-15614
  let chartToolbar: HistogramChartToolbarProps = {};
  // Config of the interactions for the old API (ChartInteractions)
  let chartInteractions = {};
  // Config of the interactinos for the new API (Histogram.Zoom)
  let slotInteractions = {};

  let intents: IntentProps[] | undefined;
  Children.forEach(children, (child) => {
    /* oxlint-disable-next-line typescript/no-deprecated */
    if (isSlot(child, ChartInteractions)) {
      const { children, ...previousInteractions } = child.props;

      const interactions = iterateChartInteractionsSlots(children);

      chartInteractions = {
        ...previousInteractions,
        ...interactions,
      };
    } else if (isSlot(child, XAxis)) {
      slots = {
        ...slots,
        xAxis: child.props,
      };
    } else if (isSlot(child, YAxis)) {
      slots.yAxis.push(child.props);
    } else if (isSlot(child, Legend)) {
      slots = {
        ...slots,
        legend: child.props,
      };
    } else if (isSlot(child, Tooltip)) {
      slots = {
        ...slots,
        tooltip: child.props,
      };
      // oxlint-disable-next-line typescript/no-deprecated
    } else if (isSlot(child, ChartToolbar)) {
      const { children, ...toolbar } = child.props;
      const childrenToolbar = iterateChartToolbarSlots(children);

      chartToolbar = {
        enabled: true,
        ...toolbar,
        ...childrenToolbar,
      };
    } else if (isSlot(child, Toolbar)) {
      slotToolbar = {
        enabled: child.props.hidden !== true,
        ...slotToolbar,
        ...child.props,
      };
    } else if (isSlot(child, Zoom)) {
      const enabled = !disabledOnlyWhenSpecified(child.props);

      slotInteractions = {
        ...slotInteractions,
        zoom: { enabled },
        zoomX: { enabled },
      };
    } else if (isSlot(child, Pan)) {
      const enabled = !disabledOnlyWhenSpecified(child.props);
      slotInteractions = { ...slotInteractions, pan: { enabled } };
      // DEPRECATION: [APPDEV-18945] BREAKING-CHANGE: Remove HistogramChart.DownloadCSV.
    } else if (isSlot(child, HistogramChartDownloadCSVSlot)) {
      slotToolbar = { ...slotToolbar, downloadData: { enabled: true } };
    } else if (isSlot(child, Intent)) {
      intents = intents ? [...intents, child.props] : [child.props];
    } else if (isSlot(child, Annotations)) {
      const { children, ...annotationsProps } = child.props;

      const { tracks, tooltip } = iterateTrackSlots(children);

      const annotations = {
        ...annotationsProps,
        tracks,
        tooltip,
      };

      slots = {
        ...slots,
        // @ts-expect-error: Strict mode transition — the `annotations` object shape (including `tooltip`) doesn't exactly match `HistogramAnnotationsConfigProps`
        annotations,
      };
    } else if (isSlot(child, ThresholdIndicator)) {
      slots.thresholds.push(child.props);
    } else if (isSlot(child, Select)) {
      slots = {
        ...slots,
        select: child.props,
      };
    } else if (isSlot(child, ColorRule)) {
      isValidColorRule(child.props) && slots.colorRules.push(child.props);
    }
  });

  const hasInteractionConfig =
    Object.keys(chartInteractions).length > 0 ||
    Object.keys(slotInteractions).length > 0;

  slots.interactions = hasInteractionConfig
    ? getInteractionsConfig(chartInteractions, slotInteractions)
    : {};

  const hasToolbarConfig =
    !isEmpty(slotToolbar) || !isEmpty(chartToolbar) || intents;

  if (hasToolbarConfig) {
    slots.toolbar = {
      ...chartToolbar,
      ...slotToolbar,
      enabled: slotToolbar.enabled ?? chartToolbar.enabled ?? true,
      intents,
    };
  }

  return slots;
};

/**
 * Generate chart config based on slots
 * @param children - CharInteraction children
 * @returns ChartInteraction slot config
 */
const iterateChartToolbarSlots = (
  children: ReactNode,
): HistogramChartToolbarConfig => {
  let toolbar = {};

  Children.forEach(children, (childToolbar) => {
    // oxlint-disable-next-line typescript/no-deprecated
    if (isSlot(childToolbar, DownloadData)) {
      // TODO APPDEV-15614: Remove deprecated DownloadData handling
      toolbar = {
        ...toolbar,
        downloadData: { enabled: true },
      };
    }
  });

  return toolbar;
};

/**
 * Generate config based on the Tracks and Markers slots
 * @param children - Annotations children
 * @returns tracks configuration including the markers
 */
const iterateTrackSlots = (children: ReactNode) => {
  const tracks: AnnotationsTrackConfig[] = [];

  let tooltip: AnnotationsTooltipConfig | undefined;

  Children.forEach(children, (child) => {
    if (isSlot(child, Track)) {
      const { children, ...trackProps } = child.props;
      const currentTrack = {
        ...trackProps,
        markers: [] as AnnotationsMarkerConfig[],
      };

      Children.forEach(children, (child) => {
        if (isSlot(child, Marker)) {
          const marker = populateMissingMarkerProps(child.props, trackProps);

          currentTrack.markers.push(marker);
        }
      });

      const track = populateMissingTrackProps(currentTrack);
      tracks.push(track);
    } else if (isSlot(child, AnnotationsTooltip)) {
      const { children, hidden } = child.props;
      tooltip = {
        template: children,
        hidden: hidden ?? false,
      };
    }
  });

  return { tracks, tooltip };
};
